/**
 * Phase 8 · Cluster C-48 · Plan card (the approval gate).
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2 row 4
 *
 * Renders a pending / executing plan beneath its assistant turn with a
 * row per step (checkbox + rationale + Reversible/Permanent badge) and
 * the four control buttons (Approve all · Approve step-by-step · Edit ·
 * Cancel). The card observes `plan.state` + `plan.stepStatuses` from
 * `useAgentThread`'s `AgentTurnPlan` and calls back through
 * `onApprove / onReject / onCancel` to drive the runner.
 *
 * Edit is intentionally a no-op for C-48 MVP — the spec asks for a
 * button, but the actual "tweak the step args" UX is queued for a
 * later cluster. We render a disabled button with a tooltip so the
 * affordance is present but doesn't lie about capabilities.
 */

import { useState } from 'react';
import { Check, CheckCircle2, AlertCircle, CircleDashed, Loader2, X, XCircle } from 'lucide-react';
import type { AgentTurnPlan } from '../hooks/useAgentThread';
import { requiresManualApproval, safetyReason } from '../safety';
import { AgentStepStatus } from './AgentStepStatus';

interface Props {
  plan: AgentTurnPlan;
  /** Called when the user clicks Approve all or Approve step-by-step. */
  onApprove: (planId: string, mode: 'all' | 'stepwise') => void;
  onReject: (planId: string) => void;
  onCancel: (planId: string) => void;
}

export function AgentPlanCard({ plan, onApprove, onReject, onCancel }: Props) {
  const [picked, setPicked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(plan.steps.map((_, i) => [i, true])),
  );

  const isTerminal =
    plan.state === 'done' ||
    plan.state === 'rejected' ||
    plan.state === 'cancelled';
  const isBusy = plan.state === 'approved' || plan.state === 'executing';
  const isFailed = plan.state === 'failed';

  const headerLabel = headerForState(plan.state);

  return (
    <div
      className="mt-2 overflow-hidden rounded-[10px] border"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-elevated)',
      }}
      role="region"
      aria-label="Proposed plan"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between border-b px-3 py-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
            Proposed plan
          </span>
          <span
            className="rounded-full px-1.5 py-[1px] text-[10px] font-medium"
            style={stateChipStyle(plan.state)}
          >
            {headerLabel}
          </span>
        </div>
        <span className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
          {plan.steps.length} step{plan.steps.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Step status strip (compact pills) */}
      <div className="px-3 pt-2">
        <AgentStepStatus plan={plan} />
      </div>

      {/* Step list */}
      <ol className="flex flex-col gap-1.5 px-3 py-2">
        {plan.steps.map((step, i) => {
          const st = plan.stepStatuses[i]?.status;
          const permanent = !step.reversible || requiresManualApproval(step.toolName);
          const reason = safetyReason(step.toolName);
          return (
            <li
              key={i}
              className="flex items-start gap-2 rounded-[8px] px-2 py-1.5"
              style={{
                background:
                  st === 'running'
                    ? 'color-mix(in srgb, var(--ggj-accent,#10B981) 8%, transparent)'
                    : 'var(--vscode-bg-hover)',
              }}
            >
              <label
                className="mt-0.5 inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded border"
                style={{
                  borderColor:
                    picked[i]
                      ? 'var(--ggj-accent,#10B981)'
                      : 'var(--vscode-border-primary)',
                  background: picked[i]
                    ? 'var(--ggj-accent,#10B981)'
                    : 'transparent',
                }}
                title={
                  isBusy || isTerminal
                    ? 'Steps lock once the plan starts executing'
                    : 'Include this step'
                }
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={picked[i] !== false}
                  disabled={isBusy || isTerminal}
                  onChange={(e) =>
                    setPicked((prev) => ({ ...prev, [i]: e.target.checked }))
                  }
                />
                {picked[i] !== false ? <Check className="h-3 w-3 text-white" aria-hidden /> : null}
              </label>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className="font-mono text-[11px]"
                    style={{ color: 'var(--ggj-accent,#10B981)' }}
                    title={step.toolName}
                  >
                    {step.toolName}
                  </span>
                  <span
                    className="rounded-full px-1.5 py-[1px] text-[10px] font-medium"
                    style={
                      permanent
                        ? {
                            background: '#FEE2E2',
                            color: '#991B1B',
                          }
                        : {
                            background: '#D1FAE5',
                            color: '#065F46',
                          }
                    }
                    title={reason ?? (permanent ? 'This action cannot be undone' : 'Easily undone')}
                  >
                    {permanent ? 'Permanent' : 'Reversible'}
                  </span>
                  <StepStateIcon status={st} />
                </div>
                <div
                  className="mt-0.5 text-[12px] leading-snug"
                  style={{ color: 'var(--vscode-text-primary)' }}
                >
                  {step.rationale}
                </div>
                {plan.stepStatuses[i]?.error ? (
                  <div
                    className="mt-0.5 text-[11px]"
                    style={{ color: '#B91C1C' }}
                  >
                    {plan.stepStatuses[i]?.error}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Failure reason / footer */}
      {isFailed && plan.failureReason ? (
        <div
          className="mx-3 mb-2 flex items-start gap-1.5 rounded-[8px] border px-2 py-1.5 text-[11px]"
          style={{
            borderColor: '#FCA5A5',
            background: '#FEF2F2',
            color: '#991B1B',
          }}
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Execution halted: {plan.failureReason}</span>
        </div>
      ) : null}

      {/* Action buttons */}
      <div
        className="flex flex-wrap items-center justify-end gap-1.5 border-t px-3 py-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        {isTerminal ? (
          <span className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
            {terminalHint(plan.state)}
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onCancel(plan.planId)}
              disabled={isBusy}
              className="inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: 'var(--vscode-text-secondary)',
              }}
            >
              <X className="h-3 w-3" aria-hidden />
              Cancel
            </button>
            <button
              type="button"
              disabled
              title="Editing plan steps arrives in a follow-up release"
              className="inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium opacity-40"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: 'var(--vscode-text-secondary)',
                cursor: 'not-allowed',
              }}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onReject(plan.planId)}
              disabled={isBusy}
              className="inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                borderColor:
                  'color-mix(in srgb, #EF4444 40%, var(--vscode-border-primary))',
                color: '#B91C1C',
              }}
            >
              <XCircle className="h-3 w-3" aria-hidden />
              Reject
            </button>
            <button
              type="button"
              onClick={() => onApprove(plan.planId, 'stepwise')}
              disabled={isBusy}
              className="inline-flex items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                borderColor:
                  'color-mix(in srgb, var(--ggj-accent,#10B981) 50%, var(--vscode-border-primary))',
                color: 'var(--ggj-accent,#10B981)',
              }}
            >
              Step-by-step
            </button>
            <button
              type="button"
              onClick={() => onApprove(plan.planId, 'all')}
              disabled={isBusy}
              className="inline-flex items-center gap-1 rounded-[8px] px-2.5 py-1 text-[12px] font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: 'var(--ggj-accent,#10B981)' }}
            >
              {isBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <CheckCircle2 className="h-3 w-3" aria-hidden />
              )}
              Approve all
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StepStateIcon({ status }: { status: AgentTurnPlan['stepStatuses'][number]['status'] | undefined }) {
  if (!status) return null;
  if (status === 'running') {
    return (
      <Loader2
        className="h-3 w-3 animate-spin"
        style={{ color: 'var(--ggj-accent,#10B981)' }}
        aria-label="Running"
      />
    );
  }
  if (status === 'done') {
    return <CheckCircle2 className="h-3 w-3" style={{ color: '#10B981' }} aria-label="Done" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-3 w-3" style={{ color: '#DC2626' }} aria-label="Failed" />;
  }
  return (
    <CircleDashed
      className="h-3 w-3"
      style={{ color: 'var(--vscode-text-tertiary)' }}
      aria-label="Pending"
    />
  );
}

function headerForState(state: AgentTurnPlan['state']): string {
  switch (state) {
    case 'pending':
      return 'Awaiting approval';
    case 'approved':
      return 'Starting…';
    case 'executing':
      return 'Executing';
    case 'done':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'rejected':
      return 'Rejected';
    case 'cancelled':
      return 'Cancelled';
  }
}

function stateChipStyle(state: AgentTurnPlan['state']): React.CSSProperties {
  switch (state) {
    case 'pending':
      return { background: '#FEF3C7', color: '#92400E' };
    case 'approved':
    case 'executing':
      return { background: '#DBEAFE', color: '#1E3A8A' };
    case 'done':
      return { background: '#D1FAE5', color: '#065F46' };
    case 'failed':
      return { background: '#FEE2E2', color: '#991B1B' };
    case 'rejected':
    case 'cancelled':
    default:
      return {
        background: 'var(--vscode-bg-hover)',
        color: 'var(--vscode-text-secondary)',
      };
  }
}

function terminalHint(state: AgentTurnPlan['state']): string {
  switch (state) {
    case 'done':
      return 'All steps completed.';
    case 'rejected':
      return 'You rejected this plan.';
    case 'cancelled':
      return 'Plan cancelled.';
    default:
      return '';
  }
}
